/* eslint-disable no-magic-numbers */
import type {
  CreateMergeIntegrationLinkResponseAPI,
  GetMergeIntegrationResponseAPI,
  GetXeroAccountCodeMappingsResponse,
  GetXeroAccountCodeMappingsResponseAPI,
  GetXeroAccountsResponse,
  GetXeroAccountsResponseAPI,
  GetXeroConnectionResponseAPI,
  GetXeroOrganisationsResponse,
  GetXeroOrganisationsResponseAPI,
  MergeIntegration,
  MergeIntegrationLink,
  UpdateXeroAccountCodeMappingsRequest,
  UpdateXeroAccountCodeMappingsRequestAPI,
  UpdateXeroAccountCodeMappingsResponse,
  UpdateXeroAccountCodeMappingsResponseAPI,
  UpdateXeroConnectionRequest,
  UpdateXeroConnectionRequestAPI,
  XeroAccountCodeMappingErrorItem,
  XeroAccountCodeMappingErrorItemAPI,
  XeroAccountCodeMappingItem,
  XeroAccountCodeMappingItemAPI,
  XeroConnection,
} from '@omnipresentgroup/types';

/** Mapping */
export const transformMergeIntegrationFromAPI = (
  integration: GetMergeIntegrationResponseAPI,
): MergeIntegration => ({
  enabled: integration.enabled,
  status: integration.status,
  companyId: integration.company_id,
  integrationName: integration.integration_name,
  icon: {
    image: integration.icon?.image,
    squareImage: integration.icon?.square_image,
  },
  magicLinkUrl: integration.magic_link_url,
  employees: integration.employees.map((employee) => ({
    id: employee.employee_id,
    firstName: employee.first_name,
    lastName: employee.last_name,
    workEmail: employee.work_email,
    syncStatus: employee.sync_status,
    lastEventAt: employee.last_event_at,
  })),
});

export const transfromMergeIntegrationLinkFromAPI = (
  integrationLink: CreateMergeIntegrationLinkResponseAPI,
): MergeIntegrationLink => ({
  mergeMagicLinkUrl: integrationLink.merge_magic_link_url,
  mergeMagicLinkUrlExpiryAt: integrationLink.merge_magic_link_url_expiry_at,
});

export const transformXeroConnectionFromAPI = (
  xeroIntegration: GetXeroConnectionResponseAPI,
): XeroConnection => ({
  connectionStatus: xeroIntegration.connection_status,
  canUserConfigureConnection: xeroIntegration.can_user_configure_connection,
  xeroOrganisation: xeroIntegration.xero_organisation,
  requiredActions: xeroIntegration.required_actions,
});

export const transformXeroConnectionUpdateRequestToAPI = (
  xeroIntegrationUpdate: UpdateXeroConnectionRequest,
): UpdateXeroConnectionRequestAPI => ({
  xero_organisation: xeroIntegrationUpdate.xeroOrganisation,
});

export const transformXeroOrganisationsFromAPI = (
  xeroOrganisationsAPI: GetXeroOrganisationsResponseAPI,
): GetXeroOrganisationsResponse =>
  xeroOrganisationsAPI.xero_organisations.map((xeroOrganisation) => ({
    id: xeroOrganisation.id,
    name: xeroOrganisation.name,
  }));

export const transformXeroAccountCodeMappingsFromAPI = (
  xeroAccountCodeMappingsAPI: GetXeroAccountCodeMappingsResponseAPI,
): GetXeroAccountCodeMappingsResponse =>
  xeroAccountCodeMappingsAPI.account_code_mappings.map(
    (accountCodeMapping) => ({
      category: {
        id: accountCodeMapping.category.id,
        name: accountCodeMapping.category.name,
        taxRate: accountCodeMapping.category.tax_rate,
      },
      ...(accountCodeMapping.xero_account_code && {
        xeroAccountCode: {
          id: accountCodeMapping.xero_account_code.id,
          name: accountCodeMapping.xero_account_code.name,
          taxRate: accountCodeMapping.xero_account_code.tax_rate,
          code: accountCodeMapping.xero_account_code.code,
        },
      }),
    }),
  );

export const transformXeroAccountsFromAPI = (
  xeroAccountsAPI: GetXeroAccountsResponseAPI,
): GetXeroAccountsResponse =>
  xeroAccountsAPI.xero_accounts
    .map((xeroAccount) => ({
      id: xeroAccount.id,
      name: xeroAccount.name,
      code: xeroAccount.code,
      description: xeroAccount.description,
      taxRate: xeroAccount.tax_rate,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

export const transformXeroAccountCodeMappingsPayloadToAPI = (
  xeroAccountCodeMappings: UpdateXeroAccountCodeMappingsRequest,
): UpdateXeroAccountCodeMappingsRequestAPI => ({
  account_code_mappings: xeroAccountCodeMappings.map(
    (xeroAccountCodeMapping) => ({
      category_id: xeroAccountCodeMapping.categoryId,
      xero_account_code_id: xeroAccountCodeMapping.xeroAccountCodeId,
    }),
  ),
});

const isXeroAccountCodeMappingError = (
  mapping: XeroAccountCodeMappingErrorItemAPI | XeroAccountCodeMappingItemAPI,
): mapping is XeroAccountCodeMappingErrorItemAPI => 'error' in mapping;

const transformErrorMapping = (
  mapping: XeroAccountCodeMappingErrorItemAPI,
): XeroAccountCodeMappingErrorItem => ({
  categoryId: mapping.category_id,
  error: {
    code: mapping.error.code,
    description: mapping.error.description,
  },
});

const transformSuccessMapping = (
  mapping: XeroAccountCodeMappingItemAPI,
): XeroAccountCodeMappingItem => ({
  category: {
    id: mapping.category.id,
    name: mapping.category.name,
    taxRate: mapping.category.tax_rate,
  },
  ...(mapping.xero_account_code && {
    xeroAccountCode: {
      id: mapping.xero_account_code.id,
      name: mapping.xero_account_code.name,
      taxRate: mapping.xero_account_code.tax_rate,
      code: mapping.xero_account_code.code,
    },
  }),
});

export const transformUpdateXeroAccountCodeMappingsResponseFromAPI = (
  updateXeroAccountCodeMappings: UpdateXeroAccountCodeMappingsResponseAPI,
): UpdateXeroAccountCodeMappingsResponse =>
  updateXeroAccountCodeMappings.account_code_mappings.map((mapping) =>
    isXeroAccountCodeMappingError(mapping)
      ? transformErrorMapping(mapping)
      : transformSuccessMapping(mapping),
  );
